import { ChangeEvent, DragEvent, useRef, useState } from 'react';

// Aligné avec backend/app/conversion/client.py _SUPPORTED_MIMES
// (Sprint 3 T8 — emails Outlook + tableurs Excel).
const ACCEPTED = [
  'application/pdf',
  // Bureautique
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/msword', // DOC
  'application/vnd.oasis.opendocument.text', // ODT
  'application/rtf',
  'text/plain',
  // Tableurs (T8)
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/vnd.ms-excel', // XLS
  'application/vnd.oasis.opendocument.spreadsheet', // ODS
  // Images
  'image/jpeg',
  'image/png',
  'image/tiff',
  // Emails (T8)
  'application/vnd.ms-outlook', // .msg
  'message/rfc822', // .eml
  // Extension fallback (libmagic retourne souvent octet-stream sur .msg —
  // le navigateur n'envoie pas le bon MIME donc on accepte aussi les
  // extensions explicites)
  '.msg',
  '.eml',
  '.xlsx',
  '.xls',
  '.ods',
].join(',');

type Props = {
  onFiles: (files: File[]) => void | Promise<void>;
  disabled?: boolean;
  hint?: string;
};

/**
 * Walks a DataTransferItem entry tree (file or directory) and emits all
 * files at any depth. Uses the non-standard but widely-supported
 * webkitGetAsEntry / FileSystemEntry API.
 */
async function readEntries(entry: FileSystemEntry, into: File[]): Promise<void> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    await new Promise<void>((resolve) => {
      fileEntry.file((f) => {
        into.push(f);
        resolve();
      });
    });
    return;
  }
  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    // readEntries returns chunks of up to 100 entries — loop until empty.
    let batch: FileSystemEntry[] = [];
    do {
      batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      for (const child of batch) {
        await readEntries(child, into);
      }
    } while (batch.length > 0);
  }
}

export default function FileDropZone({ onFiles, disabled, hint }: Props) {
  const [hover, setHover] = useState(false);
  const filesInput = useRef<HTMLInputElement>(null);
  const dirInput = useRef<HTMLInputElement>(null);

  const onDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setHover(false);
    if (disabled) return;
    const all: File[] = [];
    if (e.dataTransfer.items && typeof e.dataTransfer.items[0]?.webkitGetAsEntry === 'function') {
      // Use the entry API for folder support
      const items = Array.from(e.dataTransfer.items);
      for (const item of items) {
        const entry = item.webkitGetAsEntry?.();
        if (entry) await readEntries(entry, all);
      }
    } else if (e.dataTransfer.files) {
      // Fallback: only top-level files (no folder traversal)
      all.push(...Array.from(e.dataTransfer.files));
    }
    if (all.length > 0) await onFiles(all);
  };

  const onPicked = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length > 0) await onFiles(files);
  };

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setHover(true);
      }}
      onDragLeave={() => setHover(false)}
      onDrop={onDrop}
      className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 text-center transition ${
        hover ? 'border-accent bg-accent-lighter' : 'border-rule-strong bg-paper-elevated'
      } ${disabled ? 'opacity-60' : ''}`}
      style={{ minHeight: 180 }}
    >
      <p className="text-sm text-text-primary">
        Glisser-déposer des <strong>fichiers</strong> ou un <strong>dossier complet</strong>
      </p>
      <p className="mt-1 text-xs text-text-secondary">
        {hint ?? 'PDF, DOCX, JPG, PNG, TIFF — conversion automatique au passage.'}
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => filesInput.current?.click()}
          disabled={disabled}
          className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-1.5 text-sm hover:bg-paper-subtle disabled:opacity-50"
        >
          Choisir des fichiers
        </button>
        <button
          type="button"
          onClick={() => dirInput.current?.click()}
          disabled={disabled}
          className="rounded-md border border-rule-strong bg-paper-elevated px-3 py-1.5 text-sm hover:bg-paper-subtle disabled:opacity-50"
        >
          Choisir un dossier
        </button>
      </div>
      <input
        ref={filesInput}
        type="file"
        multiple
        accept={ACCEPTED}
        onChange={onPicked}
        className="hidden"
      />
      <input
        ref={dirInput}
        type="file"
        // webkitdirectory is non-standard but supported by Chromium, Firefox, Safari
        // @ts-expect-error — TS doesn't know about webkitdirectory
        webkitdirectory=""
        directory=""
        onChange={onPicked}
        className="hidden"
      />
    </div>
  );
}
